/**
 * OAuth Continue — branded-login step for remote MCP connectors (ChatGPT).
 *
 * A remote MCP client's OAuth flow lands here so the user signs in with their
 * ProMedic account on the SAME branded /login they already use:
 *
 *   ChatGPT → {MCP}/oauth/authorize
 *     → (redirect) {PROMEDIC}/oauth-continue?tx=<tx>
 *     → (this route) if no session, detour through /login and resume
 *     → POST {MCP}/oauth/mint-code {tx}  → { redirect }
 *     → window.location.replace(redirect)  (back to the OAuth client with ?code)
 *
 * This mirrors KaneLaunch.js, but instead of minting a Kane handoff token and
 * returning it in a URL fragment, it asks the MCP server to mint a single-use
 * OAuth authorization code and hands the browser the client redirect the MCP
 * built and validated. The `tx` is opaque (signed by the MCP); we never parse
 * it, and the redirect target is chosen by the MCP (validated against the
 * registered client), so there is no open-redirect surface here.
 *
 * @module pages/OauthContinue
 */

import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { mintOauthRedirect } from '../services/oauthContinue';

// Survives the /login detour: Login.js restores only the pathname (dropping the
// query string), so we stash the tx here and read it back on resume.
const TX_STORAGE_KEY = 'oauth_continue_tx';

const OauthContinue = () => {
  const { isAuthenticated, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [error, setError] = useState(null);
  // Guard against double-execution (StrictMode / re-renders) so the code is
  // minted and the redirect fired at most once.
  const startedRef = useRef(false);

  // Resolve tx: query param first, sessionStorage fallback (set right before
  // the login detour).
  const tx =
    new URLSearchParams(location.search).get('tx') ||
    (typeof window !== 'undefined'
      ? window.sessionStorage.getItem(TX_STORAGE_KEY)
      : null);

  useEffect(() => {
    if (loading) return;
    if (startedRef.current) return;

    if (!tx) {
      startedRef.current = true;
      setError('No se recibió la solicitud de autorización. Cerrá esta ventana e intentá conectar de nuevo.');
      return;
    }

    // No ProMedic session → stash the tx and detour through the branded login,
    // resuming this route once signed in.
    if (!isAuthenticated) {
      window.sessionStorage.setItem(TX_STORAGE_KEY, tx);
      navigate('/login', {
        replace: true,
        state: { from: { pathname: '/oauth-continue' } },
      });
      return;
    }

    // Authenticated → mint the code and hand control back to the OAuth client.
    startedRef.current = true;
    (async () => {
      try {
        const redirect = await mintOauthRedirect(tx);
        window.sessionStorage.removeItem(TX_STORAGE_KEY);
        window.location.replace(redirect);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('OAuth continue failed:', err?.message);
        window.sessionStorage.removeItem(TX_STORAGE_KEY);
        setError('No se pudo completar la autorización. Intentá conectar de nuevo desde la app.');
      }
    })();
  }, [loading, isAuthenticated, tx, navigate]);

  if (error) {
    return (
      <div className="d-flex flex-column justify-content-center align-items-center min-vh-100 p-4 text-center">
        <i className="bi bi-shield-exclamation text-danger" style={{ fontSize: '2.5rem' }} aria-hidden="true"></i>
        <h1 className="h5 mt-3 mb-2">No se pudo continuar</h1>
        <p className="text-muted" style={{ maxWidth: 420 }}>{error}</p>
        <a href="/dashboard" className="btn btn-primary mt-2">Ir a ProMedic</a>
      </div>
    );
  }

  return (
    <div className="d-flex flex-column justify-content-center align-items-center min-vh-100">
      <div className="spinner-border text-primary" role="status">
        <span className="visually-hidden">Autorizando…</span>
      </div>
      <p className="text-muted mt-3">Autorizando el acceso…</p>
    </div>
  );
};

export default OauthContinue;
